import React, { useState, useRef, useEffect, useMemo } from "react";
import "./styles/Gallery.css";
import { LoadingSpinner } from "./LoadingSpinner";
import image1 from "./gallery/image1.jpg";
import image2 from "./gallery/image2.jpg";
import image3 from "./gallery/image3.jpg";
import image4 from "./gallery/image4.jpg";
import image5 from "./gallery/image5.jpg";
import image6 from "./gallery/image6.jpg";
import image7 from "./gallery/image7.jpg";
import image8 from "./gallery/image8.jpg";
import image9 from "./gallery/image9.jpg";
import image10 from "./gallery/image10.jpg";
import image11 from "./gallery/image11.jpg";
import image12 from "./gallery/image12.jpg";
import image13 from "./gallery/image13.jpg";
import image14 from "./gallery/image14.jpg";
import image15 from "./gallery/image15.jpg";
import image16 from "./gallery/image16.jpg";
import image17 from "./gallery/image17.jpg";
import image18 from "./gallery/image18.jpg";
import image19 from "./gallery/image19.jpg";
import image20 from "./gallery/image20.jpg";
import image21 from "./gallery/image21.jpg";
import image22 from "./gallery/image22.jpg";
import image23 from "./gallery/image23.jpg";
import image24 from "./gallery/image24.jpg";
import image25 from "./gallery/image25.jpg";
import image26 from "./gallery/image26.jpg";
import "react-slideshow-image/dist/styles.css";

// Lazy Image Component
const LazyImage: React.FC<{
  src: string;
  alt: string;
  onClick: () => void;
  style?: React.CSSProperties;
}> = ({ src, alt, onClick, style }) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={imgRef}
      className="lazy-image-container"
      onClick={onClick}
      style={style}
    >
      {inView && (
        <>
          {!loaded && <div className="image-placeholder">📷</div>}
          <img
            src={src}
            alt={alt}
            onLoad={() => setLoaded(true)}
            style={{
              opacity: loaded ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />
        </>
      )}
    </div>
  );
};

export const Galerija: React.FC = () => {
  const images = useMemo(
    () => [
      image1,
      image2,
      image3,
      image4,
      image5,
      image6,
      image7,
      image8,
      image9,
      image10,
      image11,
      image12,
      image13,
      image14,
      image15,
      image16,
      image17,
      image18,
      image19,
      image20,
      image21,
      image22,
      image23,
      image24,
      image25,
      image26,
    ],
    []
  );

  const [currentImage, setCurrentImage] = useState<number | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Početni loading state

  // Preload first few images for faster initial load
  useEffect(() => {
    const preloadImages = images.slice(0, 4); // Preload first 4 images
    preloadImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);

  // Simulacija učitavanja galerije
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500); // 1.5 sekunde loading

    return () => clearTimeout(timer);
  }, []);

  const openImage = (index: number) => {
    setCurrentImage(index);
    setIsZoomed(false);
    document.body.style.overflow = "hidden";
  };

  const closeImage = React.useCallback(() => {
    setCurrentImage(null);
    setIsZoomed(false);
    document.body.style.overflow = "auto";
  }, []);

  const toggleZoom = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsZoomed(!isZoomed);
  };

  const nextImage = React.useCallback(() => {
    if (currentImage !== null) {
      setCurrentImage((currentImage + 1) % images.length);
    }
  }, [currentImage, images.length]);

  const prevImage = React.useCallback(() => {
    if (currentImage !== null) {
      setCurrentImage((currentImage - 1 + images.length) % images.length);
    }
  }, [currentImage, images.length]);

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (currentImage === null) return;

      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "Escape") closeImage();
    },
    [currentImage, nextImage, prevImage, closeImage]
  );

  React.useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="App">
      {isLoading && (
        <LoadingSpinner
          size="large"
          text="Učitavanje galerije..."
          overlay={true}
        />
      )}

      <section className="page-header-kontakt">
        <div className="container">
          <div className="row"></div>
        </div>
      </section>

      <section className="gallery-container">
        <div className="container">
          <div className="row section-heading-wrapper">
            <div className="col-md-12 col-sm-12 text-center">
              <div className="section-icon">
                <i className="fas fa-camera"></i>
              </div>
              <h2 className="section-heading-inline">
                Galerija trenutaka koji govore priču
              </h2>
              <p className="section-subtitle">
                Otkrijte ljepotu Crne Gore kroz naš objektiv – kolekciju
                fotografija koja čuva uspomene na tradicionalne vrijednosti i
                kulturno nasljeđe.
              </p>
            </div>
          </div>

          <div className="gallery-grid">
            {images.map((image, index) => (
              <div key={index} className="gallery-item">
                <LazyImage
                  src={image}
                  alt={`Galerija slika ${index + 1}`}
                  onClick={() => openImage(index)}
                  style={{ cursor: "zoom-in" }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {currentImage !== null && (
        <div className="gallery-overlay active" onClick={closeImage}>
          <img
            src={images[currentImage]}
            alt={`Galerija slika ${currentImage + 1}`}
            onClick={toggleZoom}
            className={isZoomed ? "zoomed" : ""}
            style={{ cursor: isZoomed ? "zoom-out" : "zoom-in" }}
          />
          <div className="gallery-controls">
            <button
              className="gallery-control"
              onClick={(e) => {
                e.stopPropagation();
                setIsZoomed(false);
                prevImage();
              }}
            >
              ‹
            </button>
            <button
              className="gallery-control"
              onClick={(e) => {
                e.stopPropagation();
                setIsZoomed(false);
                nextImage();
              }}
            >
              ›
            </button>
          </div>
          <button className="gallery-close" onClick={closeImage}>
            ×
          </button>
        </div>
      )}
    </div>
  );
};
